import React from 'react'
import { Animated, StyleSheet, View } from 'react-native'
import type { StyleProp, ViewStyle } from 'react-native'
import { useTheme } from 'styled-components/native'
import type { DefaultTheme } from 'styled-components/native'

import I18n from 'APP/I18n'
import { Body, Icon, PersistentIndicator, TempIndicator, TextContainer, Title } from './style'

interface OwnProps {
  containerStyle: StyleProp<ViewStyle>
  hasCriticalPacketLoss: boolean
  hasPacketLoss: boolean
}

interface VideoCallPacketLossIndicatorProps extends OwnProps {
  theme: DefaultTheme
}

interface VideoCallPacketLossIndicatorState {
  aniColor: Animated.Value
  aniOpacityPersistentIndicator: Animated.Value
  aniOpacityTempIndicator: Animated.Value
  hasCriticalPacketLoss?: boolean
  hasPacketLoss?: boolean
  shouldShowTempIndicator: boolean
  tempIndicatorTimeout?: ReturnType<typeof setTimeout>
}

class VideoCallPacketLossIndicator extends React.Component<
  VideoCallPacketLossIndicatorProps,
  VideoCallPacketLossIndicatorState
> {
  state: VideoCallPacketLossIndicatorState = {
    aniOpacityPersistentIndicator: new Animated.Value(0),
    aniOpacityTempIndicator: new Animated.Value(0),
    aniColor: new Animated.Value(0),
    shouldShowTempIndicator: true,
  }

  static getDerivedStateFromProps(
    nextProps: VideoCallPacketLossIndicatorProps,
    state: VideoCallPacketLossIndicatorState
  ): VideoCallPacketLossIndicatorState {
    const { hasPacketLoss, hasCriticalPacketLoss } = nextProps
    const hasPacketLossChanged = hasPacketLoss !== state.hasPacketLoss
    const hasCriticalPacketLossChanged = hasCriticalPacketLoss !== state.hasCriticalPacketLoss

    let { tempIndicatorTimeout } = state
    let { shouldShowTempIndicator } = state

    if (hasPacketLossChanged) {
      Animated.timing(state.aniOpacityPersistentIndicator, {
        toValue: hasPacketLoss ? 1 : 0,
        duration: 300,
        useNativeDriver: false,
      }).start()
    }

    if (hasPacketLoss && shouldShowTempIndicator) {
      shouldShowTempIndicator = false
      Animated.timing(state.aniOpacityTempIndicator, {
        toValue: 1,
        duration: 300,
        useNativeDriver: false,
      }).start()

      tempIndicatorTimeout = setTimeout(() => {
        Animated.timing(state.aniOpacityTempIndicator, {
          toValue: 0,
          duration: 500,
          useNativeDriver: false,
        }).start()
      }, 10000)
    }

    if (hasCriticalPacketLossChanged) {
      Animated.timing(state.aniColor, {
        toValue: hasCriticalPacketLoss ? 1 : 0,
        // If we went from no packet loss directly to critical, don't animate from warning to critical
        duration: hasPacketLossChanged ? 0 : 1000,
        useNativeDriver: false,
      }).start()
    }

    return {
      ...state,
      hasPacketLoss,
      hasCriticalPacketLoss,
      tempIndicatorTimeout,
      shouldShowTempIndicator,
    }
  }

  componentWillUnmount() {
    if (this.state.tempIndicatorTimeout) clearTimeout(this.state.tempIndicatorTimeout)
  }

  aniColorInterpolateConfig: Animated.InterpolationConfigType = {
    inputRange: [0, 1],
    outputRange: [
      this.props.theme.colors.packetLossWarning,
      this.props.theme.colors.packetLossCritical,
    ],
  }

  persistentIndicatorStyle = {
    opacity: this.state.aniOpacityPersistentIndicator,
    backgroundColor: this.state.aniColor.interpolate(this.aniColorInterpolateConfig),
  }

  tempIndicatorStyle = {
    opacity: this.state.aniOpacityTempIndicator,
    backgroundColor: this.state.aniColor.interpolate(this.aniColorInterpolateConfig),
  }

  renderPersistentIndicator = () => (
    <PersistentIndicator
      style={this.persistentIndicatorStyle}
      testID={
        this.props.hasPacketLoss && this.props.hasCriticalPacketLoss
          ? 'poor-connection-icon-will-be-displayed'
          : 'poor-connection-icon-wont-be-displayed'
      }
    >
      <Icon />
    </PersistentIndicator>
  )

  renderTempIndicator = () => (
    <TempIndicator
      style={this.tempIndicatorStyle}
      testID={
        this.props.hasPacketLoss && this.props.hasCriticalPacketLoss
          ? 'packet-loss-container-will-be-displayed'
          : 'packet-loss-container-wont-be-displayed'
      }
    >
      <View>
        <Icon />
      </View>
      <TextContainer>
        <Title numberOfLines={1} adjustsFontSizeToFit={true}>
          {I18n.t('VideoCallScreen.packetLossTitle')!}
        </Title>
        <Body>{I18n.t('VideoCallScreen.packetLossBody')!}</Body>
      </TextContainer>
    </TempIndicator>
  )

  render() {
    const { containerStyle } = this.props
    return (
      <View
        testID="VIDEO_CALL_PACKET_LOSS_INDICATOR"
        style={[StyleSheet.absoluteFill, containerStyle]}
      >
        {this.renderPersistentIndicator()}
        {this.renderTempIndicator()}
      </View>
    )
  }
}

const ThemedVideoCallPacketLossIndicator = (props: OwnProps) => (
  <VideoCallPacketLossIndicator {...props} theme={useTheme()} />
)

export default ThemedVideoCallPacketLossIndicator
